import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Plus, Trash2 } from 'lucide-react';
import { WEEKDAYS, nextWeekday } from '../../utils/trainingWeekday';

const BLOCK_TYPES = [
  { id: 'calentamiento', label: 'Calentamiento' },
  { id: 'principal', label: 'Trabajo principal' },
  { id: 'cardio', label: 'Cardio / pulsaciones' },
  { id: 'estiramiento', label: 'Estiramiento' },
];

function emptyExercise(blockType = 'principal') {
  return {
    block_type: blockType,
    exercise_name: '',
    sets: blockType === 'principal' ? 3 : 1,
    reps: blockType === 'principal' ? '8-12' : '6-8 min',
    rest_seconds: blockType === 'principal' ? 90 : 0,
    tempo: '2-1-2-0',
    notes: '',
  };
}

function emptyDay(dia, weekday = 'monday') {
  return {
    dia,
    nombre: `Día ${dia}`,
    weekday,
    ejercicios: [emptyExercise('calentamiento'), emptyExercise('principal')],
  };
}

export default function TrainingCoachManualRoutine({ onSaved = null, onBack = null }) {
  const [gallery, setGallery] = useState([]);
  const [meta, setMeta] = useState({
    nombre: '',
    objetivo: 'hipertrofia',
    nivel: 'intermedio',
    diasCount: 3,
  });
  const [days, setDays] = useState([emptyDay(1), emptyDay(2), emptyDay(3)]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    api.get('/training/admin/gallery')
      .then((res) => setGallery(res.data?.data || []))
      .catch(() => setGallery([]));
  }, []);

  const syncDayCount = useCallback((count) => {
    const n = Math.min(6, Math.max(1, Number(count) || 1));
    setDays((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        return [...prev, ...Array.from({ length: n - prev.length }, (_, i) => {
          let wd = prev[prev.length - 1]?.weekday || 'monday';
          for (let s = 0; s <= i; s += 1) wd = nextWeekday(wd);
          return emptyDay(prev.length + i + 1, wd);
        })];
      }
      return prev.slice(0, n);
    });
  }, []);

  const updateDay = (dayIdx, patch) => {
    setDays((prev) => prev.map((d, i) => (i === dayIdx ? { ...d, ...patch } : d)));
  };

  const updateExercise = (dayIdx, exIdx, patch) => {
    setDays((prev) => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      const ejercicios = d.ejercicios.map((ex, j) => (j === exIdx ? { ...ex, ...patch } : ex));
      return { ...d, ejercicios };
    }));
  };

  const addExercise = (dayIdx, blockType = 'principal') => {
    setDays((prev) => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      return { ...d, ejercicios: [...d.ejercicios, emptyExercise(blockType)] };
    }));
  };

  const removeExercise = (dayIdx, exIdx) => {
    setDays((prev) => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      return { ...d, ejercicios: d.ejercicios.filter((_, j) => j !== exIdx) };
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const nombre = String(meta.nombre || '').trim();
    if (!nombre) {
      setError('Escribe un nombre para la rutina.');
      return;
    }
    const dias = days.map((d, i) => ({
      dia: i + 1,
      nombre: d.nombre || `Día ${i + 1}`,
      weekday: d.weekday || '',
      ejercicios: d.ejercicios.filter((ex) => String(ex.exercise_name || '').trim()),
    })).filter((d) => d.ejercicios.length > 0);
    if (!dias.length) {
      setError('Agrega al menos un ejercicio en algún día.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/training/coach/manual-routine', {
        nombre,
        objetivo: meta.objetivo,
        nivel: meta.nivel,
        dias,
      });
      setSuccess('Rutina guardada. Aparecerá en la biblioteca y podrás asignarla como la generada por IA.');
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la rutina.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="training-light-island surface-readable ui-light-panel rounded-xl border border-stone-200 p-5 space-y-5 text-stone-900">
      <div>
        <h3 className="text-lg font-bold text-stone-900">Crear rutina manual (Training)</h3>
        <p className="text-sm text-stone-600 mt-1">
          Misma estructura que la IA y «Editar rutinas»: días con calentamiento, trabajo principal, cardio y estiramiento.
          No usa el formato D28D de clases en vivo (Tabata/HIIT).
        </p>
      </div>

      {error && <div className="training-alert--error text-sm rounded-lg p-3">{error}</div>}
      {success && <div className="bg-lime-50 border border-lime-200 text-lime-900 text-sm rounded-lg p-3">{success}</div>}

      <form onSubmit={handleSave} className="space-y-5">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="block md:col-span-2">
            <span className="label">Nombre de la rutina</span>
            <input
              className="input mt-1 w-full"
              value={meta.nombre}
              onChange={(e) => setMeta({ ...meta, nombre: e.target.value })}
              placeholder="Ej. Fuerza 3 días — manual"
              required
            />
          </label>
          <label className="block">
            <span className="label">Objetivo</span>
            <select className="input mt-1 w-full" value={meta.objetivo} onChange={(e) => setMeta({ ...meta, objetivo: e.target.value })}>
              <option value="hipertrofia">Hipertrofia</option>
              <option value="fuerza">Fuerza</option>
              <option value="resistencia">Resistencia</option>
              <option value="mantenimiento">Mantenimiento</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Nivel</span>
            <select className="input mt-1 w-full" value={meta.nivel} onChange={(e) => setMeta({ ...meta, nivel: e.target.value })}>
              <option value="principiante">Principiante</option>
              <option value="intermedio">Intermedio</option>
              <option value="avanzado">Avanzado</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Días por semana</span>
            <input
              type="number"
              min={1}
              max={6}
              className="input mt-1 w-full"
              value={meta.diasCount}
              onChange={(e) => {
                const n = Number(e.target.value);
                setMeta({ ...meta, diasCount: n });
                syncDayCount(n);
              }}
            />
          </label>
        </div>

        {days.map((day, dayIdx) => (
          <div key={day.dia} className="border border-stone-200 rounded-xl p-4 bg-white space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <input
                className="input font-semibold flex-1 min-w-[200px]"
                value={day.nombre}
                onChange={(e) => updateDay(dayIdx, { nombre: e.target.value })}
                placeholder={`Día ${dayIdx + 1}`}
              />
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <span className="whitespace-nowrap">Día de la semana</span>
                <select
                  className="input text-sm h-9 min-w-[8.5rem]"
                  value={day.weekday || ''}
                  onChange={(e) => updateDay(dayIdx, { weekday: e.target.value })}
                >
                  <option value="">Sin asignar</option>
                  {WEEKDAYS.map((w) => (
                    <option key={w.id} value={w.id}>{w.label}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap gap-1">
                {BLOCK_TYPES.map((bt) => (
                  <button
                    key={bt.id}
                    type="button"
                    className="text-xs px-2 py-1 rounded-lg border border-stone-200 hover:bg-stone-50"
                    onClick={() => addExercise(dayIdx, bt.id)}
                  >
                    <Plus className="w-3 h-3 inline mr-0.5" />
                    {bt.label}
                  </button>
                ))}
              </div>
            </div>

            {day.ejercicios.length === 0 ? (
              <p className="text-sm text-stone-500">Sin ejercicios. Usa los botones de arriba para agregar bloques.</p>
            ) : (
              <div className="space-y-2">
                {day.ejercicios.map((ex, exIdx) => (
                  <div key={`${dayIdx}-${exIdx}`} className="grid md:grid-cols-12 gap-2 items-end border-b border-stone-100 pb-2">
                    <label className="md:col-span-2 block text-xs">
                      <span className="font-semibold text-stone-600">Bloque</span>
                      <select
                        className="input mt-0.5 w-full text-xs"
                        value={ex.block_type}
                        onChange={(e) => updateExercise(dayIdx, exIdx, { block_type: e.target.value })}
                      >
                        {BLOCK_TYPES.map((bt) => (
                          <option key={bt.id} value={bt.id}>{bt.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="md:col-span-4 block text-xs">
                      <span className="font-semibold text-stone-600">Ejercicio (galería)</span>
                      <select
                        className="input mt-0.5 w-full text-xs"
                        value={ex.exercise_name}
                        onChange={(e) => updateExercise(dayIdx, exIdx, { exercise_name: e.target.value })}
                      >
                        <option value="">Seleccionar…</option>
                        {gallery.map((g) => (
                          <option key={g.id || g.name} value={g.name}>{g.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="md:col-span-1 block text-xs">
                      <span className="font-semibold text-stone-600">Series</span>
                      <input type="number" min={1} className="input mt-0.5 w-full text-xs" value={ex.sets}
                        onChange={(e) => updateExercise(dayIdx, exIdx, { sets: Number(e.target.value) })} />
                    </label>
                    <label className="md:col-span-2 block text-xs">
                      <span className="font-semibold text-stone-600">Reps / tiempo</span>
                      <input className="input mt-0.5 w-full text-xs" value={ex.reps}
                        onChange={(e) => updateExercise(dayIdx, exIdx, { reps: e.target.value })} />
                    </label>
                    <label className="md:col-span-2 block text-xs">
                      <span className="font-semibold text-stone-600">Descanso (s)</span>
                      <input type="number" min={0} className="input mt-0.5 w-full text-xs" value={ex.rest_seconds}
                        onChange={(e) => updateExercise(dayIdx, exIdx, { rest_seconds: Number(e.target.value) })} />
                    </label>
                    <button
                      type="button"
                      className="md:col-span-1 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      onClick={() => removeExercise(dayIdx, exIdx)}
                      title="Quitar ejercicio"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="flex flex-wrap gap-2 justify-end">
          {onBack && (
            <button type="button" className="btn-secondary" onClick={onBack}>Volver al asistente IA</button>
          )}
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar rutina manual'}
          </button>
        </div>
      </form>
    </div>
  );
}
